import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-dropdown-select';
import { useDebounce, useMount } from 'react-use';

import styles from '../../../../../../src/pages/welcome/Welcome.module.scss';
import Warning from '../../../../common/warning/Warning';
import { TranslateService } from './TranslateService';

export const Translate = ({ apiKey, initialLanguage, setCookie }) => {
  const [translateService, setTranslateService] = useState(undefined);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [newNodesAvailable, setNewNodesAvailable] = useState(false);

  const translateServiceRef = useRef();
  const currentLanguageRef = useRef(null);
  const newNodesAvailableRef = useRef(null);

  const getPlaceholder = languagesOptions => {
    if (languagesOptions && currentLanguage && currentLanguage !== 'en') {
      return languagesOptions.find(o => o.value === currentLanguage).label;
    }
    return 'Change language';
  };

  useMount(async () => {
    if (apiKey) {
      const service = TranslateService.getInstance(apiKey);
      service
        .init()
        .then(() => {
          translateServiceRef.current = service;
          setTranslateService(service);
          if (initialLanguage && service.languages?.has(initialLanguage)) {
            currentLanguageRef.current = initialLanguage;
            setCurrentLanguage(initialLanguage);
            setNewNodesAvailable(true);
          }
        })
        .catch(error => {
          console.error('Error initializing TranslateService:', error);
        });
    }
  });

  useDebounce(
    async () => {
      if (translateServiceRef.current && currentLanguageRef.current) {
        await translateServiceRef.current.translate(currentLanguageRef.current);
      }
    },
    300,
    [currentLanguage]
  );

  useDebounce(
    async () => {
      if (
        translateServiceRef.current &&
        newNodesAvailableRef.current &&
        currentLanguageRef.current
      ) {
        newNodesAvailableRef.current = false;
        setNewNodesAvailable(false);
        translateServiceRef.current.initOriginalTextContent();
        await translateServiceRef.current.translate(currentLanguageRef.current);
      } else if (!currentLanguageRef.current) {
        newNodesAvailableRef.current = false;
        setNewNodesAvailable(false);
      }
    },
    500,
    [newNodesAvailable]
  );

  useEffect(() => {
    const observer = new MutationObserver(async mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          newNodesAvailableRef.current = true;
          setNewNodesAvailable(true);
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      console.log('Translation observer disconnected.');
      return observer.disconnect();
    };
  }, []);

  if (!translateService || !translateService?.translate) {
    return null;
  }

  let languagesOptions = Array.from(translateService.languages, ([value, label]) => ({
    value,
    label,
  }));
  languagesOptions.splice(48, 1);
  languagesOptions.unshift({
    label: 'English',
    value: 'en',
  });

  return (
    <div>
      <div className="change-language-wrap">
        <div className="inner-container no-lang ">
          <Select
            defaultValue={languagesOptions[0].value}
            options={languagesOptions}
            name="language"
            translate="no"
            className="notranslate dropdown-language"
            searchable={false}
            placeholder={getPlaceholder(languagesOptions)}
            onChange={e => {
              currentLanguageRef.current = e[0].value;
              setCurrentLanguage(e[0].value);
              setCookie('googtranslate', e[0].value, 1000);
            }}
            value={currentLanguage}
          />
        </div>
      </div>

      {currentLanguage !== 'en' && (
        <div className="container">
          <Warning
            type={'none'}
            title={
              <span className={`${styles.warningWrap}`}>
                <p className={`embold ${styles.embold}`}>Automatic language translation</p>
                Our website uses an automatic service to translate our content into different
                languages. These translations should be used for guidance only. Visit our Other
                Languages ​​page to learn more about translation and interpreting services.
              </span>
            }
          />
        </div>
      )}
    </div>
  );
};
